import { useEffect, useState } from 'react';
import type { GameweekMeta } from '@fantasy/types';
import { api } from '../api.ts';

/** Module-level, not component state — `DeadlineCountdown` and any future
 * caller share one fetch instead of each triggering their own. `cache` holds
 * the resolved value once known; `inFlight` de-dupes concurrent mounts onto
 * the same request rather than firing one each. Deliberately a bare module
 * cache rather than a provider — see the ticket notes on why one wasn't
 * worth the blast radius here. */
let cache: GameweekMeta | null = null;
let inFlight: Promise<GameweekMeta | null> | null = null;

function fetchGameweek(): Promise<GameweekMeta | null> {
  if (cache) return Promise.resolve(cache);
  if (!inFlight) {
    inFlight = api
      .currentGameweek()
      .then((meta) => {
        cache = meta;
        return meta;
      })
      .catch(() => {
        // Not fatal — same reasoning as SquadContextBar's live-points fetch:
        // the deadline countdown is ambient furniture, not something worth
        // an error banner, so a failed fetch just leaves it unrendered.
        return null;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/**
 * The current gameweek's deadline/phase data (T-074), or `null` while it's
 * unknown — before the first fetch resolves, or permanently if it failed.
 * See the module-level cache above for why two simultaneous mounts still
 * cost one request.
 */
export function useGameweek(): GameweekMeta | null {
  const [meta, setMeta] = useState<GameweekMeta | null>(cache);

  useEffect(() => {
    if (cache) return;
    let cancelled = false;
    fetchGameweek().then((result) => {
      if (!cancelled) setMeta(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return meta;
}
